/**
 * Typed ecommerce event API — the single place the app talks to the ad tags
 * (Google gtag + Microsoft UET). Every method mirrors each event to both, so
 * callers instrument the funnel once.
 *
 * GA4 is the Google hub: `trackPurchase` emits the standard GA4 `purchase`,
 * which Google Ads imports as the conversion. Microsoft Ads imports the parallel
 * UET `purchase` conversion goal. Enhanced ("Optimized") Conversions on both are
 * powered by `setUserData` (gtag + UET hash the fields client-side) called right
 * before the purchase. Every method is a no-op when the respective tag is absent
 * (IDs unset, SSR, or ad-blocked) so callers never need to guard.
 *
 * The order-placed `purchase` is also sent server-side (GA4 Measurement Protocol
 * + Microsoft UET CAPI) keyed on the same `transaction_id`, so a blocked browser
 * still converts; each platform dedups the two. We additionally dedup
 * client-side here against Vue re-renders / double-clicks via sessionStorage.
 */
export type AnalyticsItem = {
  item_id: string;
  item_name: string;
  item_category?: string;
  price?: number;
  quantity?: number;
};

type PurchaseArgs = {
  transaction_id: string;
  value: number;
  items: AnalyticsItem[];
  currency?: string;
  coupon?: string;
};

const PURCHASE_DEDUP_KEY = 'mw_ga_purchases';

export function useAnalytics() {
  const config = useRuntimeConfig().public;
  const defaultCurrency = (config.billingCurrency as string) || 'BRL';
  const measurementId = config.gaMeasurementId as string;

  const gtag = (): ((...args: unknown[]) => void) | null => {
    if (typeof window === 'undefined') return null;
    return typeof window.gtag === 'function' ? window.gtag : null;
  };

  const event = (name: string, params: Record<string, unknown>) => {
    gtag()?.('event', name, params);
  };

  /** Microsoft UET queue (present once the bat.js tag is loaded). */
  const uetq = (): { push: (...args: unknown[]) => void } | null => {
    if (typeof window === 'undefined') return null;
    const q = (window as unknown as { uetq?: { push?: unknown } }).uetq;
    return q && typeof q.push === 'function'
      ? (q as { push: (...args: unknown[]) => void })
      : null;
  };

  /** Mirror an ecommerce event to UET (`revenue_value`/`currency` per UET's spec). */
  const uetEvent = (
    name: string,
    value: number,
    currency: string,
    extra: Record<string, unknown> = {},
  ) => {
    uetq()?.push('event', name, {
      revenue_value: Number(value.toFixed(2)),
      currency,
      ...extra,
    });
  };

  /** Resolve the GA4 client id (from `_ga`) so the server event shares the session. */
  const getClientId = (): Promise<string | undefined> => {
    const g = gtag();
    if (!g || !measurementId) return Promise.resolve(undefined);
    return new Promise((resolve) => {
      let settled = false;
      const done = (v?: string) => {
        if (!settled) {
          settled = true;
          resolve(v);
        }
      };
      // gtag('get') is async; guard with a short timeout so checkout never hangs.
      setTimeout(() => done(undefined), 800);
      try {
        g('get', measurementId, 'client_id', (id: string) => done(id));
      } catch {
        done(undefined);
      }
    });
  };

  /**
   * Set first-party user data for Enhanced Conversions from the WHMCS client
   * object (loosely typed) on BOTH platforms (gtag + UET hash it client-side).
   * Call right before `trackPurchase`.
   */
  const setUserData = (client?: Record<string, any> | null) => {
    if (!client) return;
    const email = client.email ? String(client.email).trim() : undefined;
    const phone = client.phonenumber ?? client.phonenumberformatted;
    const phoneStr = phone ? String(phone).trim() : undefined;

    const g = gtag();
    if (g) {
      const userData: Record<string, unknown> = {};
      if (email) userData.email = email;
      if (phoneStr) userData.phone_number = phoneStr;
      const address: Record<string, unknown> = {};
      if (client.firstname)
        address.first_name = String(client.firstname).trim();
      if (client.lastname) address.last_name = String(client.lastname).trim();
      if (client.postcode) address.postal_code = String(client.postcode).trim();
      const country = client.countrycode ?? client.country;
      if (country) address.country = String(country).trim();
      if (Object.keys(address).length > 0) userData.address = address;
      if (Object.keys(userData).length > 0) g('set', 'user_data', userData);
    }

    // Microsoft Ads Enhanced Conversions: pass email/phone in the `pid` object;
    // UET hashes them client-side. Raw PII never leaves the browser.
    const q = uetq();
    if (q && (email || phoneStr)) {
      const pid: Record<string, string> = {};
      if (email) pid.em = email;
      if (phoneStr) pid.ph = phoneStr;
      q.push('set', { pid });
    }
  };

  const trackViewItem = (item: AnalyticsItem, currency = defaultCurrency) => {
    event('view_item', {
      currency,
      value: item.price ?? 0,
      items: [item],
    });
    uetEvent('view_item', item.price ?? 0, currency);
  };

  const trackAddToCart = (
    items: AnalyticsItem[],
    value: number,
    currency = defaultCurrency,
  ) => {
    event('add_to_cart', { currency, value, items });
    uetEvent('add_to_cart', value, currency);
  };

  const trackBeginCheckout = (
    items: AnalyticsItem[],
    value: number,
    currency = defaultCurrency,
  ) => {
    event('begin_checkout', { currency, value, items });
    uetEvent('begin_checkout', value, currency);
  };

  const trackAddPaymentInfo = (
    items: AnalyticsItem[],
    value: number,
    currency = defaultCurrency,
  ) => {
    event('add_payment_info', { currency, value, items });
    uetEvent('add_payment_info', value, currency);
  };

  /**
   * An account was created from inside a purchase flow.
   *
   * This is the step the public configurator exists to fix: the funnel used to
   * bounce anonymous visitors to `/register` before showing them a price, and
   * most of them left there. Without this event the change is unmeasurable —
   * `purchase` alone cannot tell "they never signed up" from "they signed up
   * and did not buy", which are opposite problems with opposite remedies.
   *
   * `sign_up` is a GA4 recommended event; UET takes it as a custom one.
   */
  const trackSignUp = (method: 'password' | 'social' | 'existing-account') => {
    event('sign_up', { method });
    uetq()?.push('event', 'sign_up', { event_category: 'checkout', event_label: method });
  };

  /**
   * A search of the image catalog, and how many images it found.
   *
   * The catalog's backlog is whatever customers look for and do not find: a
   * search answered with zero results is a request for an app nobody built yet.
   * `search` is a GA4 recommended event (`search_term`); UET takes it as a
   * custom one. The caller debounces, so this is one event per settled query.
   */
  const trackSearch = (term: string, resultCount: number, surface: string) => {
    event('search', { search_term: term, result_count: resultCount, surface });
    uetq()?.push('event', 'search', {
      event_category: surface,
      event_label: term,
      event_value: resultCount,
    });
  };

  /**
   * An image chosen from a list — which one, and from which list (a curated
   * shelf, a category, a search), so the picker's shelves can be judged by what
   * is actually picked from them. `select_item` is GA4's recommended event.
   */
  const trackSelectItem = (item: AnalyticsItem, listId: string) => {
    event('select_item', { item_list_id: listId, items: [item] });
    uetq()?.push('event', 'select_item', {
      event_category: listId,
      event_label: item.item_id,
    });
  };

  /** Already-sent transaction ids in this tab (survives Vue re-renders). */
  const alreadySent = (transactionId: string): boolean => {
    if (typeof window === 'undefined') return false;
    try {
      const raw = window.sessionStorage.getItem(PURCHASE_DEDUP_KEY);
      const set = new Set<string>(raw ? JSON.parse(raw) : []);
      if (set.has(transactionId)) return true;
      set.add(transactionId);
      window.sessionStorage.setItem(
        PURCHASE_DEDUP_KEY,
        JSON.stringify([...set]),
      );
      return false;
    } catch {
      return false;
    }
  };

  const trackPurchase = (args: PurchaseArgs) => {
    if (!args.transaction_id || alreadySent(args.transaction_id)) return;
    const currency = args.currency ?? defaultCurrency;
    event('purchase', {
      transaction_id: args.transaction_id,
      value: Number(args.value.toFixed(2)),
      currency,
      ...(args.coupon ? { coupon: args.coupon } : {}),
      items: args.items,
    });
    // Microsoft Ads conversion goal (event=purchase). transaction_id is the
    // dedup key against the UET CAPI server send.
    uetEvent('purchase', args.value, currency, {
      transaction_id: args.transaction_id,
    });
  };

  /**
   * Secondary, revenue-accuracy event fired when WHMCS confirms payment. NOT a
   * conversion (Ads imports the order-placed `purchase`); kept distinct so it
   * never double-counts and is not deduped against `purchase`.
   */
  const trackPaymentConfirmed = (args: {
    transaction_id: string;
    value: number;
    currency?: string;
  }) => {
    event('payment_confirmed', {
      transaction_id: args.transaction_id,
      value: Number(args.value.toFixed(2)),
      currency: args.currency ?? defaultCurrency,
    });
  };

  return {
    getClientId,
    setUserData,
    trackViewItem,
    trackAddToCart,
    trackBeginCheckout,
    trackAddPaymentInfo,
    trackSignUp,
    trackSearch,
    trackSelectItem,
    trackPurchase,
    trackPaymentConfirmed,
  };
}

/** Convert micro-cents (catalog/quote) to major currency units. */
export function microCentsToMajor(value?: string | number | null): number {
  if (value == null) return 0;
  return Number(value) / 1_000_000;
}

/** Convert integer cents (order result) to major currency units. */
export function centsToMajor(value?: string | number | bigint | null): number {
  if (value == null) return 0;
  return Number(value) / 100;
}
